/**
 * Bookless derived targets (e.g. goalie_fpts): no target SSE books; fuse drives EntriesKF.
 */

const CI_Z = 1.64;

export function marketIdFromMarketKey(marketKey) {
  const parts = String(marketKey || '').split(':');
  return parts.length >= 3 ? parts[2] : '';
}

export function isBooklessDerivedMarket(detail, quote) {
  if (detail?.derived_model_available) {
    const rules = Array.isArray(detail.rules_config)
      ? detail.rules_config
      : Array.isArray(detail.rules)
        ? detail.rules
        : [];
    if (rules.some((r) => Boolean(r.allow_engine_without_target_books))) {
      return true;
    }
    if (detail.layer_usage?.competitor_sync_muted) {
      return true;
    }
  }
  if (
    quote &&
    quote.quote_source === 'entries' &&
    Boolean(quote.mute_competitor_signals) &&
    Number(quote.n_books ?? 0) === 0
  ) {
    return true;
  }
  return false;
}

/** Normalize league labels to Optic slug form (mirrors ``optic_league_slug_from_label``). */
export function opticLeagueSlug(raw) {
  if (raw == null) return null;
  let s = String(raw).trim().toLowerCase();
  if (!s || s === 'unknown') return null;
  s = s.replace(/\s+/g, '_').replace(/[^a-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return s || null;
}

/** True when rule has no league scope, or ``league`` matches one of ``rule.leagues``. */
export function modelRuleMatchesLeague(rule, league) {
  const leagues = Array.isArray(rule?.leagues) ? rule.leagues : [];
  if (!leagues.length) return true;
  const needle = opticLeagueSlug(league);
  if (!needle) return false;
  const raw = String(league || '').trim().toLowerCase();
  return leagues.some((lg) => {
    const entry = String(lg || '').trim();
    if (!entry) return false;
    if (entry.toLowerCase() === raw) return true;
    return (opticLeagueSlug(entry) || entry.toLowerCase()) === needle;
  });
}

/**
 * Enabled YAML model rule for this market_id + fixture league.
 * League-scoped rules (e.g. All-Star-only player_points) win over unscoped;
 * league-only rules do not match other leagues.
 */
export function findApplicableModelRule(rules, marketId, league) {
  const mid = String(marketId || '').trim();
  if (!mid) return null;
  const list = Array.isArray(rules) ? rules : [];
  const candidates = list.filter(
    (r) => r && r.enabled !== false && String(r.target_market_id || '').trim() === mid,
  );
  if (!candidates.length) return null;
  const specific = candidates.filter(
    (r) => Array.isArray(r.leagues) && r.leagues.length && modelRuleMatchesLeague(r, league),
  );
  if (specific.length) return specific[0];
  const unscoped = candidates.filter((r) => !Array.isArray(r.leagues) || !r.leagues.length);
  if (league != null && String(league).trim() !== '') {
    // League known: only unscoped rules apply (not All-Star-only on regular season).
    return unscoped[0] || null;
  }
  // League unknown: prefer unscoped; do not assume a league-scoped rule applies.
  return unscoped[0] || null;
}

/** True when any enabled rule targets this market_id (ignores league scope). */
export function marketIdHasModelRule(rules, marketId) {
  const mid = String(marketId || '').trim();
  if (!mid) return false;
  if (mid === 'goalie_fpts') return true;
  const list = Array.isArray(rules) ? rules : [];
  return list.some(
    (r) => r && r.enabled !== false && String(r.target_market_id || '').trim() === mid,
  );
}

/**
 * Whether the dashboard should open/show the Models tab for this market.
 * When league is unknown, any rule for the market_id counts (avoids Competitors flash).
 * When league is known, only an applicable rule (or goalie_fpts) counts.
 */
export function shouldOpenModelsTab(rules, marketId, league) {
  const mid = String(marketId || '').trim();
  if (!mid) return false;
  if (mid === 'goalie_fpts') return true;
  if (findApplicableModelRule(rules, mid, league) != null) return true;
  if (league == null || String(league).trim() === '') {
    return marketIdHasModelRule(rules, mid);
  }
  return false;
}

/**
 * Derived fuse targets and YAML ``model_markets`` targets that apply to this quote's league.
 * ``modelRulesOrIds`` may be the rules array from ``/api/model-markets/rules``, or a legacy
 * Set/array of target market ids (ids match any league — prefer passing full rules).
 */
export function isBooklessModelMarket(detail, quote, modelRulesOrIds) {
  if (isBooklessDerivedMarket(detail, quote)) {
    return true;
  }
  const mid =
    String(quote?.market_id || '').trim() ||
    marketIdFromMarketKey(quote?.market_key);
  if (!mid) return false;

  if (Array.isArray(modelRulesOrIds) && modelRulesOrIds.some((r) => r && r.target_market_id != null)) {
    return findApplicableModelRule(modelRulesOrIds, mid, quote?.league) != null;
  }

  const ids =
    modelRulesOrIds instanceof Set
      ? modelRulesOrIds
      : new Set(Array.isArray(modelRulesOrIds) ? modelRulesOrIds : []);
  if (!ids.size) {
    return false;
  }
  return ids.has(mid);
}

export function parseModelOutputTimestamp(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? raw / 1000 : raw;
  }
  const s = String(raw).trim();
  if (!s) return null;
  const ms = Date.parse(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
  if (!Number.isFinite(ms)) return null;
  return ms / 1000;
}

function sigmoid(x) {
  if (!Number.isFinite(x)) return null;
  if (x >= 0) {
    const z = Math.exp(-x);
    return 1 / (1 + z);
  }
  const z = Math.exp(x);
  return z / (1 + z);
}

/** 90% CI in probability space from EntriesKF posterior (log-odds x, variance P). */
export function computePosteriorCi(xPost, pVar, z = CI_Z) {
  if (xPost == null || pVar == null) {
    return { p_lower: null, p_upper: null };
  }
  const x = Number(xPost);
  const P = Number(pVar);
  if (!Number.isFinite(x) || !Number.isFinite(P) || P < 0) {
    return { p_lower: null, p_upper: null };
  }
  const sd = Math.sqrt(P);
  return {
    p_lower: sigmoid(x - z * sd),
    p_upper: sigmoid(x + z * sd),
  };
}

function findNearbyHistoryRow(rows, ts, epsilonSec = 1.0) {
  let best = null;
  let bestDelta = epsilonSec + 1;
  for (const row of rows) {
    if (row.timestamp == null) continue;
    const delta = Math.abs(Number(row.timestamp) - ts);
    if (delta <= epsilonSec && delta < bestDelta) {
      best = row;
      bestDelta = delta;
    }
  }
  return best;
}

function hasValidCi(row) {
  return (
    row != null &&
    row.p_lower != null &&
    row.p_upper != null &&
    Number.isFinite(Number(row.p_lower)) &&
    Number.isFinite(Number(row.p_upper))
  );
}

/** Merge fused model output rows into quote history so the chart updates on each fuse. */
export function enrichQuoteHistoryWithModelOutputs(quoteHistory, modelHistory, balancedLine) {
  const rows = Array.isArray(quoteHistory) ? quoteHistory.map((r) => ({ ...r })) : [];
  const byTs = new Map(rows.map((r) => [r.timestamp, r]));
  let lastLine = balancedLine != null ? Number(balancedLine) : null;

  for (const row of rows) {
    if (row.base_line != null && Number.isFinite(Number(row.base_line))) {
      lastLine = Number(row.base_line);
    }
  }

  for (const row of modelHistory || []) {
    const ts = parseModelOutputTimestamp(row.timestamp);
    if (ts == null) continue;
    const pFair = row.p_fair_after ?? row.p_fair_obs;
    if (pFair == null || !Number.isFinite(Number(pFair))) continue;

    const ci = computePosteriorCi(row.x_after, row.P_after);
    const existing = byTs.get(ts) ?? findNearbyHistoryRow(rows, ts);

    if (existing) {
      existing.p_fair = Number(pFair);
      existing.model_fuse = true;
      // Prefer kf_quotes CI when already present; never overwrite with stale/wrong values.
      if (!hasValidCi(existing) && hasValidCi(ci)) {
        existing.p_lower = ci.p_lower;
        existing.p_upper = ci.p_upper;
      }
      continue;
    }

    const pt = {
      time: ts * 1000,
      timestamp: ts,
      p_fair: Number(pFair),
      p_lower: ci.p_lower,
      p_upper: ci.p_upper,
      base_line: lastLine,
      model_fuse: true,
    };
    byTs.set(ts, pt);
    rows.push(pt);
  }

  return rows.sort((a, b) => a.timestamp - b.timestamp);
}
